import React, { useState } from "react";
import styled from "styled-components";
import TitleBlock from "../../../components/block/TitleBlock";
import ListBlock from "../../../components/block/ListBlock";
import IconButton from "../../../components/button/IconButton";
import { useCopyToClipboard } from "../../../hooks/useCopyToClipboard";
import ToolTip from "../../../components/tooltip/ToolTip";
import StatusIndicator, { StatusType } from "../../../components/status-indicator/StatusIndicator";
import SvgLoadingAnimation from "../../../components/loading";
import Modal from "../../../components/modal/Modal";
import ConfirmModal from "../../../components/modal/ConfirmModal";
import Input from "../../../components/form-item/Input";
import DateDisplay, { DATE_DISPLAY_TYPE } from "../../../components/date-display/DateDisplay";
import {
  useGetMCPApiKeysQuery,
  useCreateMCPApiKeyMutation,
  useUpdateMCPApiKeyMutation,
  useDeleteMCPApiKeyMutation,
} from "../../../app/accounts/mcpApiKeys";
import toast from "react-hot-toast";

// styled-components
const URLListItem = styled.div`
  padding: 16px;
  border-radius: 4px;
  border: 1px solid var(--soremo-border);
  background-color: #fff;
  box-shadow: 2px 4px 8px 0px rgba(0, 0, 0, 0.05);
  
  &:hover {
    background-color: var(--soremo-background);
  }
`;

const URLText = styled.div`
  flex: 1;
  font-family: monospace;
  font-size: 11px;
  border-radius: 2px;
  border: 1px solid var(--soremo-border);
  background-color: var(--soremo-background);
  padding: 12px 4px;
  color: var(--soremo-light-gray);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// APIキーデータの型定義
interface MCPApiKey {
    id: number;
    memo: string;
    api_key: string;
    mcp_url: string;
    created_at: string;
}

const MCP: React.FC = () => {
    const [status, setStatus] = useState<StatusType>('idle');
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [mcpMemo, setMcpMemo] = useState("");
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingApiKey, setEditingApiKey] = useState<{ id: number; memo: string } | null>(null);
    const [editMemo, setEditMemo] = useState("");

    // RTK Query hooks
    const { data: apiKeysResponse, isLoading, refetch } = useGetMCPApiKeysQuery();
    const [createMCPApiKey] = useCreateMCPApiKeyMutation();
    const [updateMCPApiKey] = useUpdateMCPApiKeyMutation();
    const [deleteMCPApiKey] = useDeleteMCPApiKeyMutation();

    // クリップボードコピー（旧 CopyButton コンポーネント相当）
    const { copy: copyToClipboard } = useCopyToClipboard({
        successMessage: "MCP URLをコピーしました",
    });

    // レスポンスからURLデータを取得
    const mcpApiKeys: MCPApiKey[] = apiKeysResponse?.data || [];

    // MCP URLを安全に表示（短縮表示）
    const getDisplayUrl = (url: string) => {
        const maxLength = 50;
        if (url.length <= maxLength) return url;
        return `${url.substring(0, maxLength)}...`;
    };

    // 新しいMCP URLを発行（モーダルを開く）
    const handleCreateApiKey = () => {
        setShowModal(true);
    };

    // 実際にMCP URLを発行する処理
    const handleConfirmCreate = async () => {
        setStatus('creating');
        setShowModal(false);

        try {
            // BE側でmemoが任意項目になったので、空文字列でも明示的に送信
            const trimmedMemo = mcpMemo.trim();
            const requestData = { memo: trimmedMemo };
            
            console.log("Creating API key with data:", requestData);
            await createMCPApiKey(requestData).unwrap();
            
            // 成功時にリストを再取得
            await refetch();
            
            toast.success("新しいMCP URLを発行しました");

            // 入力をクリア
            setMcpMemo("");
        } catch (error: any) {
            // より詳細なエラー情報を表示
            console.error("Failed to create MCP API key:", error);
            console.error("Error details:", JSON.stringify(error, null, 2));
            
            let errorMessage = "URLの発行に失敗しました";
            
            if (error?.data?.errors) {
                // バリデーションエラーの場合
                console.log("Validation errors:", error.data.errors);
                const errors = error.data.errors;
                if (errors.memo) {
                    errorMessage = `メモ: ${errors.memo[0]}`;
                } else {
                    errorMessage = error.data.message || errorMessage;
                }
            } else if (error?.data?.message) {
                errorMessage = error.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            toast.error(errorMessage);
        } finally {
            setStatus('idle');
        }
    };

    // モーダルをキャンセル
    const handleCancelModal = () => {
        setShowModal(false);
        setMcpMemo("");
    };

    // 用途編集モーダルを開く
    const handleEditMemo = (apiKey: MCPApiKey) => {
        setEditingApiKey({ id: apiKey.id, memo: apiKey.memo });
        setEditMemo(apiKey.memo || "");
        setShowEditModal(true);
    };

    // 用途編集を確認する
    const handleConfirmEdit = async () => {
        if (!editingApiKey) return;

        setStatus('saving');
        setShowEditModal(false);

        try {
            await updateMCPApiKey({ 
                id: editingApiKey.id, 
                memo: editMemo 
            }).unwrap();
            
            // 成功時にリストを再取得
            await refetch();
            
            toast.success("用途を更新しました");

            // 編集状態をクリア
            setEditingApiKey(null);
            setEditMemo("");
        } catch (error: any) {
            const errorMessage = error?.data?.message || error?.message || "用途の更新に失敗しました";
            toast.error(errorMessage);
            console.error("Failed to update MCP URL memo:", error);
        } finally {
            setStatus('idle');
        }
    };

    // 用途編集モーダルをキャンセル
    const handleCancelEdit = () => {
        setShowEditModal(false);
        setEditingApiKey(null);
        setEditMemo("");
    };

    // MCP URLを削除（確認は ConfirmModal で行う）
    const handleDeleteApiKey = async (id: number) => {
        setStatus('deleting');
        setDeletingId(id);
        try {
            await deleteMCPApiKey(id).unwrap();
            
            // 成功時にリストを再取得
            await refetch();
            
            toast.success("URLを削除しました");
        } catch (error: any) {
            const errorMessage = error?.data?.message || error?.message || "URLの削除に失敗しました";
            toast.error(errorMessage);
            console.error("Failed to delete MCP API key:", error);
        } finally {
            setStatus('idle');
            setDeletingId(null);
        }
    };

    return (
        <div className="u-w100 u-col u-gap24 u-relative">
            <TitleBlock
                title="Model Context Protocol"
                description="MCP接続のためのURLを発行・管理"
            />

            {/* MCP URL発行ボタン */}
            <ListBlock
                type="add"
                label="MCP URLを新規発行"
                onClick={handleCreateApiKey}
            />

            {/* 登録済みのMCP URLリスト */}
            {!isLoading && mcpApiKeys && mcpApiKeys.length > 0 && (
                <div className="u-w100 u-col u-gap16">
                    {mcpApiKeys.map((apiKey) => (
                        <URLListItem key={apiKey.id} className="u-w100">
                            <div className="u-w100 u-row u-gap32">
                                {(apiKey.memo || apiKey.created_at) && (
                                    <div className="u-col u-gap4">
                                        {apiKey.memo && (
                                            <div className="heading-13">{apiKey.memo}</div>
                                        )}
                                        <div className="label8 u-text-light-gray">
                                            <DateDisplay date={apiKey.created_at} type={DATE_DISPLAY_TYPE.DATE_TIME} />
                                        </div>
                                    </div>
                                )}
                                <div className="u-w100 u-row-between u-gap16">
                                    <div className="u-row u-gap4">
                                        <URLText>{getDisplayUrl(apiKey.mcp_url)}</URLText>
                                        <ToolTip description="MCP URLをクリップボードにコピー" placement="top">
                                            <IconButton
                                                icon="content_copy"
                                                size={16}
                                                onClick={() =>
                                                    copyToClipboard(apiKey.mcp_url)
                                                }
                                            />
                                        </ToolTip>
                                    </div>
                                    <div className="u-row u-gap8">
                                        <ToolTip description="用途を編集" placement="top">
                                            <IconButton
                                                onClick={() => handleEditMemo(apiKey)} icon="edit" />
                                        </ToolTip>
                                        <ToolTip description="MCP URLを削除" placement="top">
                                            <IconButton
                                                onClick={() => setDeleteConfirmId(apiKey.id)}
                                                disabled={status === 'deleting' && deletingId === apiKey.id} icon="delete" />
                                        </ToolTip>
                                    </div>
                                </div>
                            </div>
                        </URLListItem>
                    ))}
                </div>
            )}

            {/* ローディング状態 */}
            {isLoading && <SvgLoadingAnimation />}

            {/* ステータス表示 */}
            <StatusIndicator status={status}>
                {status === "creating" && "URLを発行中..."}
                {status === "deleting" && "URLを削除中..."}
                {status === "saving" && "URLを更新中..."}
            </StatusIndicator>

            {/* URL発行モーダル */}
            {showModal && (
                <Modal
                    onClose={handleCancelModal}
                    primaryAction={{
                        label: "MCPのURLを発行",
                        onClick: handleConfirmCreate,
                        rightIcon: "chevron_right",
                    }}
                    secondaryAction={{
                        label: "キャンセル",
                        onClick: handleCancelModal,
                    }}
                >
                    <div className="u-w100">
                        <Input
                            label="用途"
                            option="optional"
                            placeholder="Cursor / Claude / ChatGPT"
                            value={mcpMemo}
                            onChange={(e) => setMcpMemo(e.target.value)}
                            width="100%"
                        />
                    </div>
                </Modal>
            )}
            {/* 用途編集モーダル */}
            {showEditModal && (
                <Modal
                    onClose={handleCancelEdit}
                    primaryAction={{
                        label: "更新",
                        onClick: handleConfirmEdit,
                    }}
                    secondaryAction={{
                        label: "キャンセル",
                        onClick: handleCancelEdit,
                    }}
                >
                    <div className="u-w100">
                        <Input
                            label="用途"
                            option="optional"
                            placeholder="Cursor / Claude / ChatGPT"
                            value={editMemo}
                            onChange={(e) => setEditMemo(e.target.value)}
                            width="100%"
                        />
                    </div>
                </Modal>
            )}
            {/* URL削除確認モーダル */}
            {deleteConfirmId !== null && (
                <ConfirmModal
                    message={"このURLを削除しますか？\n削除後は復元できません。"}
                    confirmLabel="削除"
                    onConfirm={() => {
                        const id = deleteConfirmId;
                        setDeleteConfirmId(null);
                        void handleDeleteApiKey(id);
                    }}
                    onCancel={() => setDeleteConfirmId(null)}
                />
            )}
        </div>
    );
};

export default MCP;
